import React, {useEffect, useRef, useState} from 'react';
import {useMapsLibrary, useMap} from '@vis.gl/react-google-maps';
import {InfoWindowType, MarkerType} from '../../../types';
import {createRoot} from 'react-dom/client';
import {DNDState} from '../../Table/DNDController/DNDContext';

export type MarkerProps = {
    marker: MarkerType;
    routeId?: string;
    onDragEnd: (event: google.maps.MapMouseEvent, pointId: string, routeId: string) => void;
    onDragStart?: (event: google.maps.MapMouseEvent, pointId: string, routeId: string) => void;
    onDrag?: (event: google.maps.MapMouseEvent, pointId: string, routeId: string) => void;
    onClick: (arg: InfoWindowType) => void;
    children?: React.ReactNode;
    color?: string;
    setHoveredMarker?: (id?: string) => void;
    dndState: DNDState;
};

export const Marker = ({
    onDragEnd,
    onDrag,
    onDragStart,
    onClick,
    routeId,
    marker: markerPoint,
    color = 'white',
    children,
    setHoveredMarker,
    dndState
}: MarkerProps) => {
    const markersLibrary = useMapsLibrary('marker');
    const map = useMap();

    const markerRE = useRef<google.maps.marker.AdvancedMarkerElement>(null);
    const rootRef = useRef(null);

    const isMoving = useRef(false);

    const [isInitialized, setIsInitialized] = useState(false);

    useEffect(() => {
        if (rootRef.current || !markersLibrary) return;
        const container = document.createElement('div');
        container.style.width = '30px';
        container.style.height = '30px';
        rootRef.current = createRoot(container);

        markerRE.current = new markersLibrary.AdvancedMarkerElement({
            position: markerPoint,
            map,
            content: container,
            gmpDraggable: !markerPoint.isEnd && !markerPoint.isStart,
            gmpClickable: false
        });
    }, [markersLibrary]);

    useEffect(() => {
        return () => {
            if (markerRE.current) {
                markerRE.current.map = null;
            }
        };
    }, []);

    useEffect(() => {
        if (markerRE.current) {
            rootRef.current.render(children);
            markerRE.current.position = markerPoint;
            markerRE.current.map = map;
            !isInitialized && setIsInitialized(true);
        }

        const listenerDrag = markerRE.current?.addListener('drag', e => {
            e.domEvent.stopPropagation();
            isMoving.current = true;
        });

        return () => {
            listenerDrag?.remove();
        };
    }, [map, markerPoint, markersLibrary]);

    useEffect(() => {
        const listenerDragStart = markerRE.current?.addListener('dragstart', e => {
            markerRE.current.content.parentElement.style.display = 'none';
            onDragStart(e, markerPoint.id, routeId);
        });

        return () => {
            listenerDragStart?.remove();
        };
    }, [onDragStart, isInitialized]);

    useEffect(() => {
        const listenerDragEnd = markerRE.current?.addListener('dragend', e => {
            const isMovingCopy = isMoving.current;
            isMoving.current = false;
            markerRE.current.position = markerPoint;
            markerRE.current.content.parentElement.style.display = '';
            if (isMovingCopy) {
                onDragEnd(e, markerPoint.id, routeId);
            } else {
                onClick({event: e, point: markerPoint});
            }
        });

        const listenerClick = markerRE.current?.addListener('click', e => {
            onClick({event: e, point: markerPoint});
        });

        return () => {
            listenerDragEnd?.remove();
            listenerClick?.remove();
        };
    }, [onDragEnd, onClick, isInitialized]);

    useEffect(() => {
        const mouseOver = () => {
            if (markerPoint.route) {
                setHoveredMarker(markerPoint.id);
            }
        };

        const mouseOut = () => {
            setHoveredMarker(undefined);
        };

        if (markerRE.current) {
            if (
                dndState.isMoving &&
                dndState.movingRecords.find((x: any) => x.id === markerPoint.id)
            ) {
                isMoving.current = true;
                markerRE.current.content.parentElement.style.display = 'none';
            } else if (!dndState.isMoving) {
                isMoving.current = false;
                markerRE.current.position = markerPoint;
                markerRE.current.content.parentElement.style.display = '';
            }
        }

        markerRE.current?.content?.addEventListener('mouseover', mouseOver);
        markerRE.current?.content?.addEventListener('mouseout', mouseOut);

        return () => {
            markerRE.current?.content?.removeEventListener('mouseover', mouseOver);
            markerRE.current?.content?.removeEventListener('mouseout', mouseOut);
        };
    }, [markerRE.current, markerPoint, dndState]);

    return null;
};
